import {
  InternalRefetchQueryDescriptor,
  useApolloClient,
} from "@apollo/client";
import React from "react";
import { ItemTemplateSearchFilterPropertyFilterInput } from "../../../clients/graphqlTypes.js";
import { ItemTemplateSearchFilterPropertyFilterFragment } from "../templates/templateSearchFilters.generated.js";
import { searchFilterSlotToInput } from "./searchFilterSlots.js";
import { useTemplateSeachFiltersSection_DefineTemplateSearchFilterDefintionsMutation } from "./useTemplateSearchFiltersMutations.generated.js";

/**
 * The merged define mutation replaces a context's whole filter array, so every
 * write here re-emits the rows it does not touch. That is what keeps an
 * authored edit from dropping the expression-driven filters (and vice versa)
 * now that both live in one array.
 */
export function useTemplateSearchFiltersMutations({
  projectId,
  docId,
  itemId,
  propertyFilters,
  refetchQueries,
}: {
  projectId: string | null;
  docId: string;
  itemId: string;
  propertyFilters: ItemTemplateSearchFilterPropertyFilterFragment[];
  refetchQueries?: InternalRefetchQueryDescriptor[];
}) {
  const client = useApolloClient();

  const [defineTemplateSearchFilters] =
    useTemplateSeachFiltersSection_DefineTemplateSearchFilterDefintionsMutation(
      { client, refetchQueries }
    );

  const writeSlots = React.useCallback(
    async (propertyFilters: ItemTemplateSearchFilterPropertyFilterInput[]) => {
      await defineTemplateSearchFilters({
        variables: {
          input: {
            projectId,
            docId,
            itemId,
            templateSearchFilterDefinitions: { propertyFilters },
          },
        },
      });
    },
    [defineTemplateSearchFilters, docId, itemId, projectId]
  );

  /** Replace one filter in place, so editing a row does not reorder the list. */
  const setFilterSlot = React.useCallback(
    async (
      propertyKey: string,
      entry: ItemTemplateSearchFilterPropertyFilterInput
    ) => {
      const entries = propertyFilters.map(searchFilterSlotToInput);
      const index = propertyFilters.findIndex(f => f.key === propertyKey);

      await writeSlots(
        index === -1
          ? [...entries, entry]
          : entries.map((existing, i) => (i === index ? entry : existing))
      );
    },
    [propertyFilters, writeSlots]
  );

  /** Remove the filter outright — its slot and, with it, its expression. */
  const deleteFilterSlot = React.useCallback(
    async (propertyKey: string) => {
      await writeSlots(
        propertyFilters
          .filter(f => f.key !== propertyKey)
          .map(searchFilterSlotToInput)
      );
    },
    [propertyFilters, writeSlots]
  );

  return {
    defineTemplateSearchFilters,
    writeSlots,
    setFilterSlot,
    deleteFilterSlot,
  };
}
